import React, { createContext, useContext, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { theme } from '../Setup/Theme/theme';
import { useSelector } from 'react-redux';
import { coreTheme } from '../Setup/Theme/theme';

const ThemeModeContext = createContext({
    mode: 'light',
    toggleMode: () => {},
});

export const useThemeMode = () => {
    const context = useContext(ThemeModeContext);
    if (!context) {
        throw new Error('useThemeMode must be used within ThemeModeProvider');
    }
    return context;
};

// Theme pairs for different layout types
const getThemePair = (layoutType) => {
    switch (layoutType) {
        case 'PUBLIC':
            return {
                light: theme.publicLightTheme,
                dark: theme.publicDarkTheme,
                storageKey: 'publicThemeMode'
            };
        case 'AUTH':
            return {
                light: theme.authLightTheme,
                dark: theme.authDarkTheme,
                storageKey: 'authThemeMode'
            };
        case 'OFFICE':
            return {
                light: null, // Will be created dynamically with DB theme
                dark: null, // Will be created dynamically with DB theme
                storageKey: 'coreThemeMode'
            };
        default:
            return {
                light: theme.publicLightTheme,
                dark: theme.publicDarkTheme,
                storageKey: 'publicThemeMode'
            };
    }
};

export const ThemeModeProvider = ({ children, layoutType = 'PUBLIC' }) => {
    const themePair = getThemePair(layoutType);
    const themeString = useSelector(state => state.app.theme);
    const appTheme = themeString ? themeString : {};

    // Get initial mode from localStorage or default to 'light'
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem(themePair.storageKey);
        return savedMode === 'dark' ? 'dark' : 'light';
    });

    const toggleMode = () => {
        setMode((prevMode) => {
            const newMode = prevMode === 'light' ? 'dark' : 'light';
            localStorage.setItem(themePair.storageKey, newMode);
            return newMode;
        });
    };

    // Get the appropriate theme based on mode and layout type
    let currentTheme;
    
    if (layoutType === 'OFFICE') {
        // For core themes, merge with DB theme and set mode
        const baseCoreTheme = { ...coreTheme, ...appTheme };
        currentTheme = createTheme({
            ...baseCoreTheme,
            palette: {
                ...baseCoreTheme.palette,
                mode: mode === 'dark' ? 'dark' : 'light'
            }
        });
    } else {
        // For public and auth themes, use predefined themes
        currentTheme = mode === 'dark' ? themePair.dark : themePair.light;
    }

    return (
        <ThemeModeContext.Provider value={{ mode, toggleMode }}>
            <MuiThemeProvider theme={currentTheme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeModeContext.Provider>
    );
};

